import { Component, EventEmitter, Input, OnInit, Output, OnChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule, ReactiveFormsModule, FormGroup, FormBuilder, Validators, AbstractControl, ValidationErrors, ValidatorFn } from '@angular/forms';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { InputTextModule } from 'primeng/inputtext';
import { DropdownModule } from 'primeng/dropdown';
import { ToastModule } from 'primeng/toast';
import { MessageService } from 'primeng/api';
import { NGXLogger } from 'ngx-logger';
import { Utente } from '../../../../models/utente.model';
import { RuoloUtente } from '../../../../models/utente.ruolo.model';
import { UtentiService } from '../../../../services/utenti.service';
import { EnteEsecutore } from '../../../../models/ente.model';
import { EntiEsecutoriService } from '../../../../services/entiEsecutori.service';
import { PasswordModule } from 'primeng/password';

@Component({
  selector: 'app-modifica-utente',
  templateUrl: './modifica-utente.component.html',
  styleUrls: ['./modifica-utente.component.css'],
  standalone: true,
  imports: [
    CommonModule,
    FormsModule,
    ReactiveFormsModule,
    ButtonModule,
    DialogModule,
    InputTextModule,
    DropdownModule,
    ToastModule,
    PasswordModule
  ],
  providers: [MessageService]
})
export class ModificaUtenteComponent implements OnInit, OnChanges {
  @Input() visible: boolean = false;
  @Input() utente: Utente | null = null;
  @Output() visibleChange = new EventEmitter<boolean>();
  @Output() utenteModificato = new EventEmitter<Utente>();

  form: FormGroup;
  isNew: boolean = false;
  ruoli: RuoloUtente[] = [];
  enti: EnteEsecutore[] = [];

  constructor(
    private fb: FormBuilder,
    private utentiService: UtentiService,
    private entiService: EntiEsecutoriService,
    private messageService: MessageService,
    private logger: NGXLogger
  ) {
    this.form = this.fb.group({
      id: [null],
      nome: ['', Validators.required],
      email: ['', [Validators.required, Validators.email]],
      ruolo: [null, Validators.required],
      enteEsecutore: [null],
      password: ['', [
        Validators.minLength(8),
        this.passwordStrengthValidator()
      ]]
    });
  }

  ngOnInit() {
    this.caricaRuoli();
    this.caricaEnti();

    // Aggiungi listener per il cambio del ruolo
    this.form.get('ruolo')?.valueChanges.subscribe(ruolo => {
      if (ruolo?.descrizione === 'Operatore di UMVS') {
        this.form.get('enteEsecutore')?.disable();
      } else {
        this.form.get('enteEsecutore')?.enable();
      }
    });
  }

  ngOnChanges() {
    if (this.utente) {
      this.isNew = false;
      this.form.patchValue({
        id: this.utente.id,
        nome: this.utente.nome,
        email: this.utente.email,
        ruolo: this.utente.ruolo,
        enteEsecutore: this.utente.enteEsecutore
      });
      this.form.get('password')?.clearValidators();
      this.form.get('password')?.updateValueAndValidity();

      // Se gli enti sono già stati caricati, seleziona l'ente
      if (this.enti.length > 0 && this.utente.enteEsecutore) {
        const enteCorrispondente = this.enti.find(e => e.id === this.utente?.enteEsecutore?.id);
        if (enteCorrispondente) {
          this.form.patchValue({
            enteEsecutore: enteCorrispondente
          });
        }
      }
    } else {
      this.isNew = true;
      this.form.reset();
      const ruoloOperatore = this.ruoli.find(r => r.descrizione === 'Operatore dell\'ente esecutore');
      if (ruoloOperatore) {
        this.form.patchValue({
          ruolo: ruoloOperatore
        });
      }
      this.form.get('password')?.setValidators([
        Validators.required,
        Validators.minLength(8),
        this.passwordStrengthValidator()
      ]);
      this.form.get('password')?.updateValueAndValidity();

    }
  }

  caricaRuoli() {
    this.utentiService.getRuoli().subscribe({
      next: (ruoli) => {
        this.ruoli = ruoli;
        // Preseleziona il ruolo "Operatore dell'ente esecutore" e disabilita la modifica
        const ruoloOperatore = this.ruoli.find(r => r.descrizione === 'Operatore dell\'ente esecutore');
        this.logger.info('Ruoli', ruoloOperatore);
        if (ruoloOperatore) {
          this.form.patchValue({
            ruolo: ruoloOperatore
          });
          this.form.get('ruolo')?.disable();
          // Mantieni il valore del ruolo nel form anche quando è disabilitato
          this.form.get('ruolo')?.updateValueAndValidity();
        }

        // Se il ruolo è "Operatore di UMVS", disabilita la modifica dell'ente esecutore
        if (this.form.get('ruolo')?.value?.descrizione === 'Operatore di UMVS') {
          this.form.get('enteEsecutore')?.disable();
        }
      },
      error: (error) => {
        this.logger.error('[UTENTI] Errore nel caricamento dei ruoli:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Si è verificato un errore nel caricamento dei ruoli'
        });
      }
    });
  }

  caricaEnti() {
    this.entiService.get().subscribe({
      next: (enti) => {
        this.enti = enti;
        // Se stiamo modificando un utente, assicuriamoci che l'ente sia selezionato
        if (this.utente?.enteEsecutore) {
          const enteCorrispondente = this.enti.find(e => e.id === this.utente?.enteEsecutore?.id);
          if (enteCorrispondente) {
            this.form.patchValue({
              enteEsecutore: enteCorrispondente
            });
          }
        }
      },
      error: (error) => {
        this.logger.error('[UTENTI] Errore nel caricamento degli enti:', error);
        this.messageService.add({
          severity: 'error',
          summary: 'Errore',
          detail: 'Si è verificato un errore nel caricamento degli enti'
        });
      }
    });
  }

  onHide() {
    this.visibleChange.emit(false);
    this.form.reset();
  }

  onSubmit() {
    if (this.isFormValid()) {
      const utenteData: Utente = {
        ...this.form.getRawValue(),  // Usa getRawValue() invece di value per includere i campi disabilitati
        id: this.utente?.id
      };
      this.logger.info('Utente da creare', utenteData);
      if (this.isNew) {
        this.utentiService.crea(utenteData).subscribe({
          next: (utente) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Successo',
              detail: 'Utente creato con successo'
            });
            this.utenteModificato.emit(utente);
            this.onHide();
          },
          error: (error) => {
            this.logger.error('[UTENTI] Errore nella creazione dell\'utente:', error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Si è verificato un errore nella creazione dell\'utente'
            });
          }
        });
      } else if (this.utente?.id) {
        this.utentiService.modifica(utenteData).subscribe({
          next: (utente) => {
            this.messageService.add({
              severity: 'success',
              summary: 'Successo',
              detail: 'Utente modificato con successo'
            });
            this.utenteModificato.emit(utente);
            this.onHide();
          },
          error: (error) => {
            this.logger.error(`[UTENTI] Errore nella modifica dell'utente ${this.utente?.id}:`, error);
            this.messageService.add({
              severity: 'error',
              summary: 'Errore',
              detail: 'Si è verificato un errore nella modifica dell\'utente'
            });
          }
        });
      }
    } else {
      this.messageService.add({
        severity: 'error',
        summary: 'Errore',
        detail: 'Compila tutti i campi obbligatori'
      });
    }
  }

  passwordStrengthValidator(): ValidatorFn {
    return (control: AbstractControl): ValidationErrors | null => {
      const value = control.value;

      if (!value) {
        return null;
      }

      const hasUpperCase = /[A-Z]+/.test(value);
      const hasLowerCase = /[a-z]+/.test(value);
      const hasNumeric = /[0-9]+/.test(value);
      const hasSpecialChar = /[!@#$%^&*()_+\-=\[\]{};':"\\|,.<>\/?]+/.test(value);

      const passwordValid = hasUpperCase && hasLowerCase && hasNumeric && hasSpecialChar && value.length >= 8;

      return !passwordValid ? { weakPassword: true } : null;
    }
  }

  isFormValid(): boolean {
    const nomeValid = this.form.get('nome')?.valid ?? false;
    const emailValid = this.form.get('email')?.valid ?? false;
    const ruoloValue = this.form.get('ruolo')?.value;
    const ruoloValid = ruoloValue !== null && ruoloValue !== undefined;

    // Se siamo in modalità creazione, controlla anche la password
    if (this.isNew) {
      const passwordValid = this.form.get('password')?.valid ?? false;
      return nomeValid && emailValid && ruoloValid && passwordValid;
    }


    return nomeValid && emailValid && ruoloValid;
  }
}
